import { useEffect, useRef } from 'react';
import { LANGUAGE_LABELS } from '../i18n.js';
import CommandIcon from './CommandIcon.jsx';

export default function SettingsDialog({ language, onLanguageChange, preferences, onChange, onClose, t }) {
  const dialogRef = useRef(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    dialog.querySelector('button')?.focus();
    const handleKey = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); }
      if (event.key !== 'Tab') return;
      const items = [...dialog.querySelectorAll('button:not(:disabled), input:not(:disabled), [tabindex="0"]')];
      const first = items[0], last = items.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div className="settings-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section ref={dialogRef} className="settings-dialog" role="dialog" aria-modal="true" aria-labelledby="settings-title" data-testid="settings-dialog">
        <header className="settings-dialog__header">
          <CommandIcon name="gear" /><h2 id="settings-title">{t('settings.title')}</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label={t('settings.close')} data-testid="settings-close"><CommandIcon name="x" /></button>
        </header>
        <div className="settings-dialog__body">
          <fieldset className="settings-section">
            <legend><CommandIcon name="globe" />{t('app.language')}</legend>
            <div className="language-options" data-testid="language-options">
              {Object.entries(LANGUAGE_LABELS).map(([code, label]) => (
                <label key={code} className={`language-option${language === code ? ' is-selected' : ''}`}>
                  <input type="radio" name="language" value={code} checked={language === code} onChange={() => onLanguageChange(code)} />
                  <span>{label}</span>{language === code && <CommandIcon name="check" />}
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset className="settings-section">
            <legend><CommandIcon name="sun" />{t('settings.appearance')}</legend>
            <div className="settings-theme-options">
              {['dark', 'light'].map((theme) => (
                <button key={theme} type="button" aria-pressed={preferences.theme === theme} className={preferences.theme === theme ? 'is-selected' : ''}
                  onClick={() => onChange({ theme })} data-testid={`theme-${theme}`}><CommandIcon name={theme === 'dark' ? 'moon' : 'sun'} />{t(`settings.${theme}`)}</button>
              ))}
            </div>
          </fieldset>
          <fieldset className="settings-section">
            <legend><CommandIcon name="map-trifold" />{t('settings.mapDisplay')}</legend>
            {['featureLabels', 'trackerLabels', 'teamLabels', 'trails', 'teamAreas'].map((key) => (
              <div className="settings-row" key={key}>
                <label htmlFor={`preference-${key}`} id={`setting-${key}`}>{t(`settings.${key}`)}</label>
                <button id={`preference-${key}`} type="button" role="switch" aria-checked={preferences[key]} aria-labelledby={`setting-${key}`}
                  className="settings-switch" onClick={() => onChange({ [key]: !preferences[key] })} data-testid={`setting-${key}`}><span /></button>
              </div>
            ))}
            <div className="settings-label-scale">
              <label htmlFor="label-scale">{t('settings.labelSize')}</label><output htmlFor="label-scale">{Math.round(preferences.labelScale * 100)}%</output>
              <input id="label-scale" type="range" min="0.7" max="1.6" step="0.1" value={preferences.labelScale}
                onChange={(event) => onChange({ labelScale: Number(event.target.value) })} />
            </div>
          </fieldset>
        </div>
        <footer className="settings-dialog__footer"><span>{t('settings.saved')}</span><button type="button" className="button button--primary" onClick={onClose}>{t('settings.done')}</button></footer>
      </section>
    </div>
  );
}
